import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import {
  locationDayReportsGetEmployeeReport,
  locationEndofdaySettingsUpdateEmployeeReportSettings,
  printerGetAllByLocationId,
} from "~/api-refs";
import { convexClient } from "~/integrations/convex/provider";
import { toConvexTableId } from "~/lib/convex-table-id";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

import { DateInput } from "../ui/date-input";
import { EmployeeReportPreview } from "./employee-report-sections";
import { EmployeeReportSettingsFields } from "./employee-report-settings-fields";
import type {
  EmployeeReportData,
  EmployeeReportSettings,
} from "./employee-report-types";
import {
  checkEmployeeReportHasRealData,
  computeEmployeeVisibility,
  formatEmployeeReportForDisplay,
  toPrinterDeviceId,
} from "./employee-report-types";

// Re-export for consumers
export type { EmployeeReportSettings } from "./employee-report-types";

export function EndOfDayEmployeeSettingsForm({
  settings,
  locationId,
  location,
  employeeId,
  employee,
}: {
  settings: EmployeeReportSettings | null;
  locationId: string;
  location: {
    id: string;
    name: string;
    address: {
      id: string;
      addressLine1: string;
      addressLine2: string | null;
      city: string;
      zipCode: number;
      state: string;
    } | null;
  };
  employeeId: string | null;
  employee: {
    firstName: string;
    lastName: string;
  } | null;
}) {
  const dateForm = useForm({
    defaultValues: {
      reportDate: new Date(),
    },
  });

  const currentDate = dateForm.watch("reportDate");

  const { data: printers } = useQuery(
    convexQuery(printerGetAllByLocationId, {
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const {
    data: report,
    isPending: isLoadingReport,
    error: reportError,
  } = useQuery(
    convexQuery(
      locationDayReportsGetEmployeeReport,
      employeeId
        ? {
            locationId: toConvexTableId("locations", locationId),
            employeeId: toConvexTableId("employees", employeeId),
            date: currentDate.getTime(),
          }
        : "skip"
    )
  );

  const updateSettingsMutation = useMutation({
    mutationFn: (data: {
      locationId: Id<"locations">;
      value: {
        showOrdersAndBillsSummary: boolean;
        showSalesCategoriesSummary: boolean;
        showDevicesSummary: boolean;
        showVoidedItems: boolean;
        showDiscountedItems: boolean;
        showRefundedItems: boolean;
        showManagerCodes: boolean;
        showEmployeeHours: boolean;
        showEmployeeTips: boolean;
        reprintEmployeeReportWhenClockingOut: boolean;
        showDividers: boolean;
        printerDeviceId: "all" | Id<"printers">;
        printAutomaticallyOnClockOut: boolean;
        printCopies: number;
      };
    }) =>
      convexClient.mutation(
        locationEndofdaySettingsUpdateEmployeeReportSettings,
        data
      ),
    onSuccess: () => {
      ProductEvents.settingsUpdated("employee-report");
    },
  });

  // Sample data for preview when no report is available
  const previewData: EmployeeReportData = {
    employeeId,
    hoursWorked: "0.00",
    weekHours: "0.00",
    cashTips: "0.00",
    cardTips: "0.00",
    totalTips: "0.00",
    ordersCreated: 0,
    billsClosed: 0,
    averageBillAmount: "0.00",
    totalSales: "0.00",
    startCash: "0.00",
    salesByCategory: [
      {
        categoryId: "1",
        categoryName: "Food",
        amount: "0.00",
        quantity: 0,
      },
      {
        categoryId: "2",
        categoryName: "Beverages",
        amount: "0.00",
        quantity: 0,
      },
      {
        categoryId: "3",
        categoryName: "Desserts",
        amount: "0.00",
        quantity: 0,
      },
    ],
    deviceTransactions: {
      cashCollected: "0.00",
      cashSales: "0.00",
      cashDeposits: "0.00",
      cashWithdrawals: "0.00",
      difference: "0.00",
      inputByEmployeeId: employeeId,
    },
    voidedItems: [
      {
        reason: "Customer Complaint",
        amount: "0.00",
        permissionCode: {
          employeeId: "MGR001",
          employeeName: "John Manager",
        },
      },
      {
        reason: "Wrong Order",
        amount: "0.00",
        permissionCode: {
          employeeId: "MGR002",
          employeeName: "Jane Supervisor",
        },
      },
    ],
    discountedItems: [
      {
        reason: "Senior Discount",
        amount: "0.00",
        permissionCode: {
          employeeId: "MGR001",
          employeeName: "John Manager",
        },
      },
    ],
    refundedItems: [
      {
        reason: "Quality Issue",
        amount: "0.00",
        permissionCode: {
          employeeId: "MGR002",
          employeeName: "Jane Supervisor",
        },
      },
    ],
  };

  // Use actual report data if available and has meaningful data, otherwise use preview data
  const hasRealData = checkEmployeeReportHasRealData(report);

  // Use preview data if no real data exists, format backend data for display
  const displayData: EmployeeReportData =
    hasRealData && report
      ? formatEmployeeReportForDisplay(report, employeeId)
      : previewData;

  // Add a notice when showing preview data
  const isPreviewData = !(hasRealData || isLoadingReport);

  const form = useForm<EmployeeReportSettings>({
    defaultValues: settings ?? {
      showEmployeeHours: true,
      showEmployeeTips: true,
      showOrdersAndBillsSummary: true,
      showSalesCategoriesSummary: true,
      showDevicesSummary: true,
      showVoidedItems: true,
      showDiscountedItems: true,
      showRefundedItems: true,
      showManagerCodes: true,
      reprintEmployeeReportWhenClockingOut: true,
      showDividers: true,
      printerDeviceId: "all",
      printAutomaticallyOnClockOut: true,
      printCopies: 1,
    },
  });

  // Watch all toggle values
  const [formValues, setFormValues] = useState<EmployeeReportSettings>(
    form.getValues()
  );

  // Update form values when settings change
  useEffect(() => {
    if (settings) {
      form.reset(settings);
      setFormValues(settings);
    }
  }, [settings, form]);

  const update = useDebouncedCallback(async () => {
    const values = form.getValues();
    try {
      await updateSettingsMutation.mutateAsync({
        locationId: toConvexTableId("locations", locationId),
        value: {
          ...values,
          printerDeviceId: toPrinterDeviceId(values.printerDeviceId),
        },
      });
    } catch (error) {
      logger.exception("employee_report_settings_update_failed", error);
    }
  }, 1000);

  // Use form values directly for visibility control
  const {
    showEmployeeHours,
    showEmployeeTips,
    showOrdersAndBillsSummary,
    showSalesCategoriesSummary,
    showDevicesSummary,
    showVoidedItems,
    showDiscountedItems,
    showRefundedItems,
    showManagerCodes,
    showDividers,
  } = formValues;

  const handleToggleChange = (
    name: keyof EmployeeReportSettings,
    checked: boolean
  ) => {
    form.setValue(name, checked, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setFormValues((prev) => ({
      ...prev,
      [name]: checked,
    }));
    void update();
  };

  // Determine visibility conditions
  const {
    shouldShowSalesCategories,
    shouldShowDevices,
    shouldShowVoided,
    shouldShowDiscounted,
    shouldShowRefunded,
  } = computeEmployeeVisibility(
    {
      showSalesCategoriesSummary,
      showDevicesSummary,
      showVoidedItems,
      showDiscountedItems,
      showRefundedItems,
    },
    isPreviewData,
    displayData
  );

  return (
    <div className="flex items-start gap-6">
      <EmployeeReportPreview
        currentDate={currentDate}
        displayData={displayData}
        employee={employee}
        employeeId={employeeId}
        hasRealData={hasRealData}
        isLoadingReport={isLoadingReport}
        isPreviewData={isPreviewData}
        location={location}
        reportError={reportError}
        shouldShowDevices={!!shouldShowDevices}
        shouldShowDiscounted={!!shouldShowDiscounted}
        shouldShowRefunded={!!shouldShowRefunded}
        shouldShowSalesCategories={!!shouldShowSalesCategories}
        shouldShowVoided={!!shouldShowVoided}
        showDividers={showDividers}
        showEmployeeHours={showEmployeeHours}
        showEmployeeTips={showEmployeeTips}
        showManagerCodes={showManagerCodes}
        showOrdersAndBillsSummary={showOrdersAndBillsSummary}
      />

      <div className="w-full flex-1" id="receipt-info">
        <div className="mb-6">
          <h3 className="text-foreground mb-2 font-medium text-balance">
            Select Report Date
          </h3>
          <FormProvider {...dateForm}>
            <DateInput name="reportDate" placeholder="Select a date" />
          </FormProvider>
        </div>

        <EmployeeReportSettingsFields
          form={form}
          handleToggleChange={handleToggleChange}
          locationId={locationId}
          printers={printers}
          updateSettingsMutation={updateSettingsMutation}
        />
      </div>
    </div>
  );
}
